import { Component, OnInit, inject } from "@angular/core";
import { Router } from "@angular/router";
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from "@angular/forms";
import { NgGlyph } from "@ng-icons/core";
import { AppButtonComponent } from "../../../../Common/Components/Button/ButtonComponent";
import { AppCardComponent } from "../../../../Common/Components/Card/CardComponent";
import { AppAvatarComponent } from "../../../../Common/Components/Avatar/AvatarComponent";
import { AutocompleteComponent } from "../../../../Common/Components/Autocomplete/AutocompleteComponent";
import { UserEffects } from "../../State/UserEffect";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { RoleListItem } from "../../../Role/Dto/RoleModel";
import { RoleEffects } from "../../../Role/State/RoleEffect";
import { RoleState } from "../../../Role/State/RoleState";
import { FileApi } from "../../../File/Api/FileApi";
import { CreateUserRequest } from "../../Dto/UserCreateRequest";
import { AutocompleteOption } from "../../../../Common/Components/Autocomplete/Type/AutocompleteTypes";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";

@Component({
  selector: "app-user-create",
  standalone: true,
  imports: [ReactiveFormsModule, AppButtonComponent, AppCardComponent, AppAvatarComponent, NgGlyph, AutocompleteComponent],
  templateUrl: "./UserCreatePage.html",
  styleUrl: "./UserCreatePage.scss",
})
export class UserCreatePage implements OnInit {
  private readonly router = inject(Router);
  private readonly userEffects = inject(UserEffects);
  private readonly toastEffects = inject(ToastEffects);
  private readonly roleEffects = inject(RoleEffects);
  private readonly roleState = inject(RoleState);
  private readonly fileApi = inject(FileApi);
  private readonly fb = inject(FormBuilder);

  profileForm: FormGroup;
  passwordForm: FormGroup;

  roles: RoleListItem[] = [];
  roleOptions: AutocompleteOption[] = [];
  isSaving = false;

  constructor() {
    this.profileForm = this.fb.group({
      name: ["", [Validators.required, Validators.minLength(2)]],
      email: ["", [Validators.required, Validators.email]],
      phone: [""],
      address: [""],
      role: ["", [Validators.required]],
      imageUrl: [""],
    });
    this.passwordForm = this.fb.group(
      {
        password: ["", [Validators.required, Validators.minLength(8)]],
        confirmPassword: ["", [Validators.required, Validators.minLength(8)]],
      },
      { validators: passwordMatchValidator },
    );
  }

  async ngOnInit(): Promise<void> {
    await this.loadRoles();
  }

  get avatarName(): string {
    return this.profileForm.get("name")?.value || "New user";
  }

  get avatarEmail(): string {
    return this.profileForm.get("email")?.value || "No email yet";
  }

  get avatarImageUrl(): string | null {
    const value = this.profileForm.get("imageUrl")?.value;
    return (() => {
      if (value) {
        return value;
      }
      return null;
    })();
  }

  private async loadRoles(): Promise<void> {
    try {
      await this.roleEffects.searchRoles();
      this.roles = this.roleState.data();
      this.roleOptions = this.roles.map((role) => ({
        label: role.name,
        value: role.id,
      }));
    } catch (exception) {
      console.error("Error loading roles:", exception);
    }
  }

  onRoleChange(value: string | number | (string | number)[]): void {
    if (Array.isArray(value)) {
      return;
    }
    this.profileForm.patchValue({ role: value });
  }

  async saveUser(): Promise<void> {
    if (this.profileForm.invalid || this.passwordForm.invalid) {
      this.profileForm.markAllAsTouched();
      this.passwordForm.markAllAsTouched();
      this.toastEffects.show("Please fill in all required fields.", "error");
      return;
    }

    const { password } = this.passwordForm.value;
    if (!password) {
      this.toastEffects.show("Password is required.", "error");
      return;
    }

    this.isSaving = true;

    const formData = this.profileForm.value;
    const payload: CreateUserRequest = {
      name: formData.name,
      email: formData.email,
      password,
      role: formData.role || undefined,
      phone: formData.phone ?? "",
      address: formData.address ?? "",
    };

    const imageUrl = (() => {
      if (typeof formData.imageUrl === "string") {
        return formData.imageUrl.trim();
      }
      return "";
    })();
    if (imageUrl) {
      payload.imageUrl = imageUrl;
    }

    try {
      await this.userEffects.createUser(payload);
      this.toastEffects.show("User created successfully.", "success");
      await this.router.navigate(["/users"]);
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to create user."), "error");
    } finally {
      this.isSaving = false;
    }
  }

  async onAvatarChange(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];
    try {
      const uploadedFile = await this.fileApi.uploadFile(file, { folder: "users" });
      this.profileForm.patchValue({ imageUrl: uploadedFile.publicUrl });
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Failed to upload image"), "error");
    } finally {
      input.value = "";
    }
  }

  removeAvatar(): void {
    this.profileForm.patchValue({ imageUrl: "" });
  }

  goBack(): void {
    this.router.navigate(["/users"]).catch(() => undefined);
  }
}

function passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
  const current = control.get("password")?.value;
  const confirm = control.get("confirmPassword")?.value;
  if (!current || !confirm) {
    return null;
  }
  return (() => {
    if (current === confirm) {
      return null;
    }
    return { passwordMismatch: true };
  })();
}
